/**
 * Frontend Logger Utility
 * 
 * Production-safe logging system for React/TypeScript frontend
 * Automatically disables debug/info logs in production builds
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogData {
    [key: string]: any;
}

class Logger {
    private isDevelopment: boolean;
    private enabledLevels: Set<LogLevel>;

    constructor() {
        this.isDevelopment = process.env.NODE_ENV === 'development';

        // In production, only allow warn and error
        this.enabledLevels = new Set<LogLevel>(
            this.isDevelopment
                ? ['debug', 'info', 'warn', 'error']
                : ['warn', 'error']
        );
    }

    /**
     * Format log arguments into a readable message
     */
    private formatArgs(...args: any[]): string {
        const timestamp = new Date().toISOString();
        const formatted = args.map(arg => {
            if (typeof arg === 'object' && arg !== null) {
                try {
                    return JSON.stringify(arg, null, 2);
                } catch {
                    return String(arg);
                }
            }
            return String(arg);
        }).join(' ');

        return `[${timestamp}] ${formatted}`;
    }

    /**
     * Sanitize sensitive data before logging
     */
    private sanitizeData(data: any): any {
        if (!data || typeof data !== 'object') {
            return data;
        }

        const sensitiveFields = [
            'password',
            'token',
            'jwt',
            'secret',
            'apikey',
            'authorization',
            'refreshtoken',
            'accesstoken'
        ];

        const sanitized = Array.isArray(data) ? [...data] : { ...data };

        for (const key in sanitized) {
            const lowerKey = key.toLowerCase();

            if (sensitiveFields.some(field => lowerKey.includes(field))) {
                sanitized[key] = '[REDACTED]';
            } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
                sanitized[key] = this.sanitizeData(sanitized[key]);
            }
        }

        return sanitized;
    }

    /**
     * Sanitize arguments array
     */
    private sanitizeArgs(args: any[]): any[] {
        return args.map(arg => {
            if (typeof arg === 'object' && arg !== null) {
                return this.sanitizeData(arg);
            }
            return arg;
        });
    }

    /**
     * Generic log method - accepts variable arguments
     */
    private log(level: LogLevel, ...args: any[]): void {
        if (!this.enabledLevels.has(level)) {
            return; // Skip if level not enabled
        }

        const sanitizedArgs = this.sanitizeArgs(args);
        const prefix = `[${level.toUpperCase()}]`;

        // Use appropriate console method
        switch (level) {
            case 'debug':
                console.log(prefix, ...sanitizedArgs);
                break;
            case 'info':
                console.info(prefix, ...sanitizedArgs);
                break;
            case 'warn':
                console.warn(prefix, ...sanitizedArgs);
                break;
            case 'error':
                console.error(prefix, ...sanitizedArgs);
                break;
        }
    }

    /**
     * Debug level - Development only
     * Use for debugging information that's helpful during development
     * Accepts any number of arguments
     */
    debug(...args: any[]): void {
        this.log('debug', ...args);
    }

    /**
     * Info level - Development only
     * Use for general informational messages
     * Accepts any number of arguments
     */
    info(...args: any[]): void {
        this.log('info', ...args);
    }

    /**
     * Warning level - Always shown
     * Use for warning messages that don't break functionality
     * Accepts any number of arguments
     */
    warn(...args: any[]): void {
        this.log('warn', ...args);
    }

    /**
     * Error level - Always shown
     * Use for errors that need attention
     * Accepts any number of arguments
     */
    error(...args: any[]): void {
        // Special handling for Error objects
        const processedArgs = args.map(arg => {
            if (arg instanceof Error) {
                return {
                    message: arg.message,
                    stack: arg.stack,
                    name: arg.name
                };
            }
            return arg;
        });

        this.log('error', ...processedArgs);
    }

    /**
     * API call logging helper
     */
    apiCall(method: string, url: string, status?: number, data?: any): void {
        const logData = {
            method,
            url,
            status,
            ...data
        };

        if (status && status >= 400) {
            this.error(`API Error: ${method} ${url}`, logData);
        } else {
            this.debug(`API Call: ${method} ${url}`, logData);
        }
    }

    /**
     * Component lifecycle logging
     */
    lifecycle(component: string, event: 'mount' | 'unmount' | 'update', data?: any): void {
        this.debug(`[${component}] ${event}`, data);
    }

    /**
     * User action logging
     */
    userAction(action: string, data?: any): void {
        this.info(`User Action: ${action}`, data);
    }
}

// Export singleton instance
export const logger = new Logger();

// Export type for consumers
export type { LogLevel, LogData };

/**
 * MIGRATION GUIDE - How to replace console.log:
 * 
 * console.log('message') → logger.debug('message')
 * console.log('message', data) → logger.debug('message', data)
 * console.log('key:', value, 'key2:', value2) → logger.debug('key:', value, 'key2:', value2)
 * console.info('message') → logger.info('message')
 * console.warn('message') → logger.warn('message')
 * console.error('message', error) → logger.error('message', error)
 * console.error(error) → logger.error(error)
 * 
 * The logger now accepts any number of arguments, just like console.log!
 * 
 * Special cases:
 * - Socket events: logger.debug('[Socket] event received', data)
 * - API calls: logger.apiCall('GET', '/api/workplaces', 200)
 * - Component lifecycle: logger.lifecycle('WorkplaceDetail', 'mount')
 * - User actions: logger.userAction('workplace_created', { id: 123 })
 */
